import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { t } from '../../i18n.js'
import useValuationPulse from '../hooks/useValuationPulse.js'
import {
  EM_DASH,
  formatDeduction,
  formatIsoDate,
  formatMovement,
  formatPercent,
  formatWhole,
  formatYears,
  noiLabel,
  purchasersCostsHelperParts,
  salesCostsHelperParts
} from '../valuation_summary/format.js'

// EVA-296: the Valuation Summary tab. Presentation only — every figure arrives
// computed from valuation_summary.json (one calculation site, shared with the
// floating Value Conclusion panel), and this component owns order, labels and
// formatting. Unavailable values are em dashes, never zeros.
export default function ValuationSummaryTab({ valuationId, stateUrl, translations }) {
  const fetchRef = useRef(null)
  // Monotonic sequence guard, same shape as CashFlowReport: a slow mount
  // fetch must not overwrite the fresher realtime refetch that overtook it.
  const requestSeqRef = useRef(0)
  const appliedSeqRef = useRef(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [modal, setModal] = useState(null) // 'acquisition' | 'sale' | null

  const label = useCallback((key, vars) => t(key, translations, vars || {}), [translations])

  // Stable identity: the modal's focus-restore cleanup keys off this, and an
  // inline closure would re-run it on every cable-driven refetch.
  const closeModal = useCallback(() => setModal(null), [])

  const fetchSummary = useCallback(async ({ spinner = true } = {}) => {
    if (spinner) setLoading(true)

    const seq = (requestSeqRef.current += 1)

    try {
      const res = await fetch(stateUrl, { headers: { Accept: 'application/json' } })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || `Request failed (${res.status})`)
      if (seq < appliedSeqRef.current) return
      appliedSeqRef.current = seq
      setData(json.data)
      setError(null)
    } catch (e) {
      if (seq < appliedSeqRef.current) return
      setError(e?.message || label('error_fallback'))
    } finally {
      if (spinner) setLoading(false)
    }
  }, [stateUrl, label])

  useEffect(() => { fetchRef.current = fetchSummary }, [fetchSummary])

  useEffect(() => { fetchSummary() }, [fetchSummary])

  // Shared ValuationChannel — any upstream edit reprices the conclusion. The
  // refetch stays silent (no `spinner`), because the mount spinner replaces the
  // whole tab with a centred throbber and doing that on every upstream edit
  // would throw the page away under the reader. EVA-300 marks the figures as
  // provisional instead: `recalculating` dims them in place from the pulse
  // until the refetch lands, so the pause stops reading as a final number.
  const refetch = useCallback(() => fetchRef.current?.({ spinner: false }), [])
  const recalculating = useValuationPulse(valuationId, refetch)

  const headline = data?.headline || {}
  const bridge = data?.bridge || {}
  const kpis = data?.kpis || {}
  const annual = data?.annual_cash_flow || { rows: [], totals: {} }
  const terminal = data?.terminal || {}
  const sensitivity = data?.sensitivity || { available: false, rows: [] }

  const resolveHelper = useCallback(
    (parts) => parts && `(${parts.map((part) => part.text ?? label(part.key, part.vars)).join(' + ')})`,
    [label]
  )

  const purchasersHelper = useMemo(() => {
    const helper = bridge.purchasers_costs_helper
    if (!helper) return null
    return resolveHelper(purchasersCostsHelperParts({
      ratePercent: helper.rate_percent,
      hasFixed: helper.has_fixed
    }))
  }, [bridge.purchasers_costs_helper, resolveHelper])

  const salesHelper = useMemo(() => {
    const helper = terminal.sales_costs_helper
    if (!helper) return null
    return resolveHelper(salesCostsHelperParts({
      agspRatePercent: helper.agsp_rate_percent,
      nspRatePercent: helper.nsp_rate_percent,
      hasFixed: helper.has_fixed
    }))
  }, [terminal.sales_costs_helper, resolveHelper])

  const noiRowLabel = useMemo(() => {
    const info = terminal.noi_label
    if (!info) return EM_DASH
    const resolved = noiLabel({
      method: info.method,
      selectedNoiYear: info.selected_noi_year,
      periodStart: info.period_start,
      periodEnd: info.period_end
    })
    return label(resolved.key, resolved.vars)
  }, [terminal.noi_label, label])

  const acquisitionTotal = Number(bridge.acquisition_apportionment_total || 0)

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{label('loading')}</span>
        </div>
      </div>
    )
  }

  return (
    // aria-busy, not a live region: the tab holds dozens of figures and every
    // upstream edit changes most of them, so announcing each settle would be
    // unusable. See the same choice in FloatingValueConclusion.
    <div className={`valuation-summary-tab${recalculating ? ' valuation-summary-tab--recalculating' : ''}`}
         aria-busy={recalculating}>
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      {headline.alert && (
        <div className="alert alert-warning mb-3">{label(`alerts.${headline.alert}`)}</div>
      )}

      <div className="card mb-3">
        <div className="card-body">
          <div className="vs-headline-label text-muted text-uppercase">
            {label('headline.rounded_dcf_net_value')}
            {recalculating && (
              <span className="recalculating-spinner spinner-border spinner-border-sm" aria-hidden="true" />
            )}
          </div>
          <div className="vs-headline-value" data-testid="vs-headline">
            {formatWhole(headline.rounded_dcf_net_value)}
          </div>
          <hr />
          <div className="row">
            <SupportMetric label={label('headline.dcf_net_value')} value={formatWhole(headline.dcf_net_value)} />
            <SupportMetric label={label('headline.dcf_gross_value')} value={formatWhole(headline.dcf_gross_value)} />
            <SupportMetric
              label={label('headline.value_per_area')}
              value={formatWhole(headline.value_per_area)}
            />
          </div>
        </div>
      </div>

      <div className="vs-pair mb-3">
        <div className="card">
          <div className="card-header"><h5 className="card-title mb-0">{label('bridge.title')}</h5></div>
          <div className="card-body">
            <ValueRow muted label={label('bridge.pv_operating_cash_flows')} value={formatWhole(bridge.pv_operating_cash_flows)} />
            <ValueRow muted label={label('bridge.pv_terminal_value')} value={formatWhole(bridge.pv_terminal_value)} />
            {acquisitionTotal > 0 && (
              <ValueRow
                muted
                label={label('bridge.acquisition_apportionment')}
                value={formatWhole(bridge.acquisition_apportionment_total)}
                detail={
                  <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => setModal('acquisition')}>
                    {label('view_detail')}
                  </button>
                }
              />
            )}
            <ValueRow divider label={label('bridge.dcf_gross_value')} value={formatWhole(bridge.dcf_gross_value)} />
            <ValueRow
              muted
              label={label('bridge.purchasers_costs')}
              helper={purchasersHelper}
              value={formatDeduction(bridge.purchasers_costs)}
            />
            <ValueRow label={label('bridge.dcf_net_value')} value={formatWhole(bridge.dcf_net_value)} />
            <ValueRow divider strong label={label('bridge.rounded_dcf_net_value')} value={formatWhole(bridge.rounded_dcf_net_value)} />
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h5 className="card-title mb-0">{label('kpis.title')}</h5></div>
          <div className="card-body">
            <ValueRow label={label('kpis.contracted_rent')} value={formatWhole(kpis.contracted_rent)} />
            <ValueRow label={label('kpis.market_rent')} value={formatWhole(kpis.market_rent)} />
            <ValueRow label={label('kpis.noi_y1')} value={formatWhole(kpis.noi_y1)} />
            <ValueRow label={label('kpis.niy')} value={formatPercent(kpis.niy_percent)} />
            <ValueRow label={label('kpis.vacancy_area')} value={formatPercent(kpis.vacancy_area_percent)} />
            <ValueRow label={label('kpis.wault_break')} value={formatYears(kpis.wault_break_years)} />
            <ValueRow label={label('kpis.discount_rate')} value={formatPercent(kpis.discount_rate_percent)} />
            <ValueRow label={label('kpis.exit_cap_rate')} value={formatPercent(kpis.exit_cap_rate_percent)} />
          </div>
        </div>
      </div>

      <div className="vs-pair mb-3">
        <div className="card">
          <div className="card-header"><h5 className="card-title mb-0">{label('operating_pv.title')}</h5></div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm mb-0 vs-table vs-table--operating-pv">
                <thead>
                  <tr>
                    <th>{label('operating_pv.period')}</th>
                    <th className="text-end">{label('operating_pv.cash_flow')}</th>
                    <th className="text-end">{label('operating_pv.pv_of_cash_flow')}</th>
                  </tr>
                </thead>
                <tbody>
                  {annual.rows.length === 0 && (
                    <tr><td colSpan={3} className="text-muted text-center py-4">{label('operating_pv.empty')}</td></tr>
                  )}
                  {annual.rows.map((row) => (
                    <tr key={row.year_number}>
                      <td>{label('operating_pv.year', { year: row.year_number })}</td>
                      <td className="text-end">{formatWhole(row.cash_flow)}</td>
                      <td className="text-end">{formatWhole(row.pv_cash_flow)}</td>
                    </tr>
                  ))}
                  {annual.rows.length > 0 && (
                    <tr className="vs-total-row">
                      <td>{label('operating_pv.total')}</td>
                      <td className="text-end">{formatWhole(annual.totals.cash_flow)}</td>
                      <td className="text-end">{formatWhole(annual.totals.pv_cash_flow)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h5 className="card-title mb-0">{label('terminal.title')}</h5></div>
          <div className="card-body">
            <ValueRow label={noiRowLabel} value={formatWhole(terminal.base_noi)} />
            <ValueRow muted label={label('terminal.gross_up_adjustment')} value={formatWhole(terminal.gross_up_adjustment)} />
            <ValueRow divider label={label('terminal.noi_to_capitalise')} value={formatWhole(terminal.noi_to_capitalise)} />
            <ValueRow muted label={label('terminal.exit_cap_rate')} value={formatPercent(terminal.exit_cap_rate_percent)} />
            <ValueRow divider label={label('terminal.gross_sale_price')} value={formatWhole(terminal.gross_sale_price)} />
            <ValueRow muted label={label('terminal.capital_deductions')} value={formatDeduction(terminal.capital_deductions_total)} />
            <ValueRow label={label('terminal.adjusted_gross_sale_price')} value={formatWhole(terminal.adjusted_gross_sale_price)} />
            <ValueRow
              muted
              label={label('terminal.sales_costs')}
              helper={salesHelper}
              value={formatDeduction(terminal.sales_costs)}
            />
            <ValueRow divider label={label('terminal.net_sale_price')} value={formatWhole(terminal.net_sale_price)} />
            <ValueRow
              muted
              label={label('terminal.rent_apportionment_at_sale')}
              value={formatDeduction(terminal.rent_apportionment_at_sale)}
              detail={Number(terminal.rent_apportionment_at_sale || 0) > 0 && (
                <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => setModal('sale')}>
                  {label('view_detail')}
                </button>
              )}
            />
            <ValueRow divider label={label('terminal.proceeds_from_sale')} value={formatWhole(terminal.proceeds_from_sale)} />
            <ValueRow muted label={label('terminal.discount_rate')} value={formatPercent(terminal.discount_rate_percent)} />
            <ValueRow divider label={label('terminal.pv_of_net_sale_price')} value={formatWhole(terminal.pv_of_net_sale_price)} />
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header"><h5 className="card-title mb-0">{label('sensitivity.title')}</h5></div>
        <div className="card-body">
          {!sensitivity.available && (
            <div className="text-muted text-center py-4">{label('sensitivity.unavailable')}</div>
          )}
          {sensitivity.available && (
            <div className="table-responsive vs-sensitivity-scroll" tabIndex={0}>
              <table className="table table-sm table-bordered mb-0 vs-sensitivity">
                <thead>
                  <tr>
                    <th className="vs-sensitivity-corner" rowSpan={2} colSpan={2} aria-hidden="true" />
                    <th colSpan={9} className="text-center">{label('sensitivity.exit_cap_rate')}</th>
                  </tr>
                  <tr>
                    {/* EVA-300: the column rate axis is derived too — stepping the
                        exit cap rate shifts every heading — so it dims with the
                        figures. Its own class rather than vs-sensitivity-rate,
                        which carries the sticky left column and would drag these
                        headings out of place. */}
                    {sensitivity.rows[0].cells.map((cell) => (
                      <th key={cell.exit_rate_percent} className="text-end vs-sensitivity-rate-column">
                        {formatPercent(cell.exit_rate_percent)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sensitivity.rows.map((row, index) => (
                    <tr key={row.discount_rate_percent}>
                      {index === 0 && (
                        <th rowSpan={9} className="vs-sensitivity-axis">
                          <span>{label('sensitivity.discount_rate')}</span>
                        </th>
                      )}
                      <th className="text-end vs-sensitivity-rate">{formatPercent(row.discount_rate_percent)}</th>
                      {row.cells.map((cell) => (
                        <td
                          key={cell.exit_rate_percent}
                          className={`text-end vs-sensitivity-cell${cell.base ? ' vs-sensitivity-base' : ''}`}
                        >
                          <div className={cell.base ? 'fw-bold' : ''}>{formatWhole(cell.rounded_value)}</div>
                          <div className="vs-sensitivity-movement text-muted">
                            {cell.base ? (
                              <span className="vs-sensitivity-base-marker">{label('sensitivity.base')}</span>
                            ) : (
                              formatMovement(cell.movement)
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <ApportionmentModal
          kind={modal}
          data={modal === 'acquisition' ? data?.acquisition_apportionment : data?.rent_apportionment_at_sale}
          label={label}
          onClose={closeModal}
        />
      )}
    </div>
  )
}

function SupportMetric({ label, value }) {
  return (
    <div className="col-12 col-md-4">
      <div className="text-muted small">{label}</div>
      <div className="vs-support-value">{value}</div>
    </div>
  )
}

function ValueRow({ label, value, helper, detail, muted, strong, divider }) {
  return (
    <div className={`vs-row d-flex justify-content-between align-items-baseline${divider ? ' vs-row-divider' : ''}`}>
      <span className={muted ? 'text-muted' : ''}>
        {label}
        {helper && <span className="vs-cost-helper text-muted"> {helper}</span>}
        {detail}
      </span>
      <span className={`vs-row-value${strong ? ' fw-bold' : ''}`}>{value}</span>
    </div>
  )
}

// The shared apportionment detail modal, one column set for both sides. Only
// the day-count column's heading differs between them — "Purchaser Days" at
// acquisition, "Incoming Buyer Days" at sale, the same concept on the two
// sides of the same handover. Title, description and the total label are
// per-`kind` too.
function ApportionmentModal({ kind, data, label, onClose }) {
  const rows = data?.rows || []

  // Escape closes and hands focus back to whatever opened the modal — the
  // same dismissal contract the floating panel keeps.
  useEffect(() => {
    const opener = document.activeElement
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [onClose])

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1} role="dialog" onClick={onClose}>
        <div
          className="modal-dialog modal-xl modal-dialog-centered"
          role="document"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{label(`apportionment.${kind}.title`)}</h5>
              <button type="button" className="btn-close" aria-label={label('apportionment.close')} onClick={onClose} />
            </div>
            <div className="modal-body">
              <p className="text-muted small">{label(`apportionment.${kind}.description`)}</p>
              <div className="table-responsive">
                <table className="table table-sm vs-table">
                  <thead>
                    <tr>
                      <th>{label('apportionment.tenant')}</th>
                      <th>{label('apportionment.payment_schedule')}</th>
                      <th>{label('apportionment.period_start')}</th>
                      <th>{label('apportionment.period_end')}</th>
                      <th className="text-end">{label('apportionment.period_rent')}</th>
                      <th className="text-end">{label(`apportionment.${kind}.buyer_days`)}</th>
                      <th className="text-end">{label('apportionment.total_days')}</th>
                      <th className="text-end">{label('apportionment.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${row.tenant_id}-${row.period_start}`}>
                        <td>{row.tenant_name}</td>
                        <td>{label(`payment_schedules.${row.payment_schedule}`)}</td>
                        <td>{formatIsoDate(row.period_start)}</td>
                        <td>{formatIsoDate(row.period_end)}</td>
                        <td className="text-end">{formatWhole(row.period_rent)}</td>
                        <td className="text-end">{row.buyer_days}</td>
                        <td className="text-end">{row.total_days}</td>
                        <td className="text-end">{formatWhole(row.apportionment)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="vs-total-row">
                      <td colSpan={7}>{label(`apportionment.${kind}.total`)}</td>
                      <td className="text-end">{formatWhole(data?.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                {label('apportionment.close')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}
